import { useRef, useEffect } from 'react';
import { useMutation } from 'react-query';
import { getSpeechAudioByText } from 'services/inference/getSpeechAudioByText';

export const cleanTextForSpeech = (text: string): string => {
  let cleaned = text.replace(/```[\s\S]*?```/g, '');

  cleaned = cleaned.replace(/`[^`]*`/g, '');
  cleaned = cleaned.replace(/^\|.*\|$/gm, '');
  cleaned = cleaned.replace(/^[\s|:-]+$/gm, '');
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');
  cleaned = cleaned.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1');
  cleaned = cleaned.replace(/_{1,3}([^_]+)_{1,3}/g, '$1');
  cleaned = cleaned.replace(/~~([^~]+)~~/g, '$1');
  cleaned = cleaned.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]*\)/g, '');
  cleaned = cleaned.replace(/^>\s?/gm, '');
  cleaned = cleaned.replace(/^[-*_]{3,}$/gm, '');
  cleaned = cleaned.replace(/^[\s]*[-*+]\s+/gm, '');
  cleaned = cleaned.replace(/^[\s]*\d+\.\s+/gm, '');
  cleaned = cleaned.replace(/[\u{1F600}-\u{1F64F}]/gu, '');
  cleaned = cleaned.replace(/[\u{1F300}-\u{1F5FF}]/gu, '');
  cleaned = cleaned.replace(/[\u{1F680}-\u{1F6FF}]/gu, '');
  cleaned = cleaned.replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '');
  cleaned = cleaned.replace(/[\u{2600}-\u{26FF}]/gu, '');
  cleaned = cleaned.replace(/[\u{2700}-\u{27BF}]/gu, '');
  cleaned = cleaned.replace(/[\u{FE00}-\u{FE0F}]/gu, '');
  cleaned = cleaned.replace(/[\u{1F900}-\u{1F9FF}]/gu, '');
  cleaned = cleaned.replace(/[\u{1FA00}-\u{1FA6F}]/gu, '');
  cleaned = cleaned.replace(/[\u{1FA70}-\u{1FAFF}]/gu, '');
  cleaned = cleaned.replace(/[\u{200D}]/gu, '');

  return cleaned.trim();
};

export const getFirstAndLastLines = (text: string): string => {
  const cleaned = cleanTextForSpeech(text);

  const lines = cleaned
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);

  if (lines.length === 0) return '';
  if (lines.length === 1) return lines[0];

  const first = lines[0];
  const last = lines[lines.length - 1];

  if (first === last) return first;

  return `${first}\n${last}`;
};

type UseSpeechParams = {
  onSpeechAudioEnd: () => void;
  onSpeechAudioError?: (error: any) => void;
};

type GetSpeechOptions = {
  onComplete?: () => void;
  onError?: (error: any) => void;
};

export const useSpeech = ({ onSpeechAudioEnd, onSpeechAudioError }: UseSpeechParams) => {
  const playsoundRef = useRef<AudioBufferSourceNode | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sessionCompleteCallbackRef = useRef<any>(new Map());
  const sessionErrorCallbackRef = useRef<any>(new Map());
  // Helper to get or create AudioContext, recreate if closed
  const getAudioContext = (): AudioContext => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      return audioContextRef.current;
    }
    audioContextRef.current = new AudioContext();
    return audioContextRef.current;
  };

  const {
    mutate: getSpeechMutate,
    isLoading,
    reset,
  } = useMutation<any, any, { text:string, language?:"en-US"|"es-MX", onComplete?:()=>void, onError?:(error:any)=>void }>(
    (args) => {
      // Increment session token for this new request
      sessionRef.current += 1;
      // Store per-session completion callback if provided
      if (args.onComplete) {
        sessionCompleteCallbackRef.current.set(sessionRef.current, args.onComplete);
      }
      // Store per-session error callback if provided
      if (args.onError) {
        sessionErrorCallbackRef.current.set(sessionRef.current, args.onError);
      }
      abortControllerRef.current = new AbortController();
      const processedText = getFirstAndLastLines(args.text);
      return getSpeechAudioByText({
        text: processedText.slice(0, 3000),
        signal: abortControllerRef.current.signal,
        language: args.language });
    },
  {
    onSuccess: async (data) => {
      // Capture session token at entry
      const myToken = sessionRef.current;

      try {
        const buffer = await data.arrayBuffer();
        // Check token after await
        if (myToken !== sessionRef.current) return;

        const ctx = getAudioContext();
        const decodedAudio = await ctx.decodeAudioData(buffer);
        // Check token after await
        if (myToken !== sessionRef.current) return;

        // Resume context if suspended
        if (ctx.state === 'suspended') {
          await ctx.resume();
          // Check token after await
          if (myToken !== sessionRef.current) return;
        }

        const playsound = ctx.createBufferSource();
        playsound.buffer = decodedAudio;
        playsound.connect(ctx.destination);

        // Check token before start
        if (myToken !== sessionRef.current) return;

        playsound.start(ctx.currentTime);
        playsoundRef.current = playsound;

        // Set onended callback with token guard
        playsound.onended = () => {
          // Only notify if this session is still current
          if (myToken === sessionRef.current) {
            playsoundRef.current = null;
            // Invoke per-session completion callback if registered
            const sessionCallback = sessionCompleteCallbackRef.current.get(myToken);
            if (sessionCallback) {
              sessionCallback();
              sessionCompleteCallbackRef.current.delete(myToken);
            }
            onSpeechAudioEnd();
          }
        };
      } catch (error) {
        // Handle errors from current session
        if (myToken === sessionRef.current) {
          // Invoke per-session error callback if registered
          const sessionErrorCallback = sessionErrorCallbackRef.current.get(myToken);
          if (sessionErrorCallback) {
            sessionErrorCallback(error);
            sessionErrorCallbackRef.current.delete(myToken);
          } else if (onSpeechAudioError) {
            onSpeechAudioError(error);
          }
          // Clean up completion callback if it exists
          sessionCompleteCallbackRef.current.delete(myToken);
        }
      }
    },
    onError: (error) => {
      // Handle fetch-level errors (AbortError, network errors, etc.)
      const myToken = sessionRef.current;

      // Check if this is an AbortError (user-initiated stop)
      if (error?.name === 'AbortError') {
        // Silently ignore AbortError — user intentionally stopped
        sessionErrorCallbackRef.current.delete(myToken);
        sessionCompleteCallbackRef.current.delete(myToken);
        return;
      }

      // For real errors, invoke error callback if registered
      const sessionErrorCallback = sessionErrorCallbackRef.current.get(myToken);
      if (sessionErrorCallback) {
        sessionErrorCallback(error);
        sessionErrorCallbackRef.current.delete(myToken);
      } else if (onSpeechAudioError) {
        onSpeechAudioError(error);
      }
      // Clean up completion callback if it exists
      sessionCompleteCallbackRef.current.delete(myToken);
    },
  }
  );

  const stopSpeechAudio = () => {
    // Increment token to invalidate any pending operations
    sessionRef.current += 1;

    if (playsoundRef.current) {
      try {
        playsoundRef.current.onended = null;
      } catch {
        // Ignore errors detaching handler
      }
      try {
        playsoundRef.current.stop();
      } catch {
        // Ignore errors stopping already-stopped node
      } finally {
        playsoundRef.current = null;
      }
    }
  };

  const abortGetSpeech = () => {
    // Increment token to invalidate pending onSuccess
    sessionRef.current += 1;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    reset();
  };

  // Like abortGetSpeech but skips reset() so isSpeechLoading stays true
  // during a stop-old/start-new replacement sequence in playSpeech.
  const abortGetSpeechSilent = () => {
    sessionRef.current += 1;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // Cleanup on unmount: close AudioContext
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const getSpeech = (args: { text: string, language?: "en-US" | "es-MX" }, opts?: GetSpeechOptions) => {
    getSpeechMutate({ ...args, onComplete: opts?.onComplete, onError: opts?.onError });
  };

  return {
    getSpeech,
    stopSpeechAudio,
    isSpeechLoading: isLoading,
    abortGetSpeech,
    abortGetSpeechSilent,
  };
};
